// frontend/src/pages/AziendaDettaglio.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

const AziendaDettaglio = () => {
  const { code } = useParams();
  const nav = useNavigate();

  const [azienda, setAzienda] = useState(null);
  const [loading, setLoading] = useState(true);

  // toggle modifica campi anagrafica
  const [modifica, setModifica] = useState(false);
  const [formData, setFormData] = useState({});

  // credenziali accesso (ruolo AZIENDA)
  const [cred, setCred] = useState({ username: "", password: "", email: "" });
  const [savingCred, setSavingCred] = useState(false);

  useEffect(() => {
    const fetchAzienda = async () => {
      try {
        const res = await fetch(`${API_URL}/api/aziende/${code}/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
        });
        if (!res.ok) throw new Error("Errore nel caricamento");
        const data = await res.json();
        setAzienda(data);
        setFormData(data);

        // suggerimenti auto per le credenziali
        const suggestedUser =
          (data.code || data.name || "")
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "");
        setCred((c) => ({
          ...c,
          username: c.username || suggestedUser,
          email: c.email || data.email || "",
        }));
      } catch (e) {
        alert("❌ Errore caricamento azienda");
      } finally {
        setLoading(false);
      }
    };
    fetchAzienda();
  }, [code]);

  const aggiornaAzienda = async () => {
    try {
      const res = await fetch(`${API_URL}/api/aziende/${code}/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Errore aggiornamento");
      alert("✅ Azienda aggiornata!");
      setModifica(false);
      // ricarico i dati mostrati
      setAzienda((a) => ({ ...a, ...formData }));
    } catch (e) {
      alert("❌ Errore aggiornamento");
    }
  };

  const eliminaAzienda = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare questa azienda?")) return;
    try {
      const res = await fetch(`${API_URL}/api/aziende/${code}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
      });
      if (!res.ok) throw new Error("Errore eliminazione");
      alert("✅ Azienda eliminata.");
      nav("/aziende");
    } catch (e) {
      alert("❌ Errore eliminazione");
    }
  };

  const salvaCredenziali = async () => {
    if (!cred.username.trim() || !cred.password.trim()) {
      alert("Inserisci username e password.");
      return;
    }
    setSavingCred(true);
    try {
      const res = await fetch(`${API_URL}/api/aziende/${code}/credenziali/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: cred.username.trim(),
          password: cred.password,
          email: cred.email.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Errore impostazione credenziali");
      alert(`✅ Credenziali salvate per l'utente: ${data.user?.username || cred.username}`);
      // per sicurezza, svuoto solo la password
      setCred((c) => ({ ...c, password: "" }));
    } catch (e) {
      alert(`❌ ${e.message || "Errore impostazione credenziali"}`);
    } finally {
      setSavingCred(false);
    }
  };

  if (loading) return <p>⏳ Caricamento...</p>;
  if (!azienda) return <p>❌ Azienda non trovata</p>;

  return (
    <div className="container mt-4">
      {/* Header azioni */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button onClick={() => nav("/aziende")} className="btn btn-outline-secondary">
          ← Torna alla lista
        </button>
        {!modifica ? (
          <button className="btn btn-primary" onClick={() => setModifica(true)}>
            ✏️ Modifica
          </button>
        ) : (
          <>
            <button className="btn btn-success me-2" onClick={aggiornaAzienda}>
              ✅ Aggiorna
            </button>
            <button className="btn btn-danger me-2" onClick={eliminaAzienda}>
              ❌ Elimina
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModifica(false);
                setFormData(azienda);
              }}
            >
              🔙 Annulla
            </button>
          </>
        )}
      </div>

      {/* Dati anagrafici */}
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <strong>Dettaglio azienda</strong> <span className="text-muted ms-2">({azienda.code})</span>
        </div>
        <div className="card-body">
          {[
            ["name", "Nome Azienda"],
            ["vat", "Partita IVA"],
            ["email", "Email"],
            ["phone", "Telefono"],
            ["address", "Indirizzo"],
            ["ref_contact", "Contatto Referente"],
            ["unique_code", "Codice Univoco"],
            ["notes", "Note"],
          ].map(([key, label]) => (
            <div className="mb-3" key={key}>
              <label className="form-label fw-bold">{label}:</label>
              {modifica ? (
                key === "notes" ? (
                  <textarea
                    className="form-control"
                    value={formData[key] || ""}
                    onChange={(e) => setFormData((f) => ({ ...f, [key]: e.target.value }))}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={formData[key] || ""}
                    onChange={(e) => setFormData((f) => ({ ...f, [key]: e.target.value }))}
                  />
                )
              ) : (
                <p className="mb-0">{azienda[key] || "—"}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Credenziali di accesso */}
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Accesso azienda (username/password)</strong>
          <span className="text-muted small">
            Crea/aggiorna l’utente collegato a <em>{azienda.name}</em> (ruolo <code>AZIENDA</code>).
          </span>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-control"
                value={cred.username}
                onChange={(e) => setCred((c) => ({ ...c, username: e.target.value }))}
                placeholder="es. az0001 o lincotrobar"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                value={cred.password}
                onChange={(e) => setCred((c) => ({ ...c, password: e.target.value }))}
                placeholder="Imposta o reimposta password"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Email (opzionale)</label>
              <input
                type="email"
                className="form-control"
                value={cred.email}
                onChange={(e) => setCred((c) => ({ ...c, email: e.target.value }))}
                placeholder="Email per recupero/contatti"
              />
            </div>
          </div>

          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                const sugg =
                  (azienda.code || azienda.name || "")
                    .toString()
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, "");
                setCred((c) => ({ ...c, username: sugg || c.username }));
              }}
            >
              🪄 Suggerisci username
            </button>
            <button className="btn btn-primary" disabled={savingCred} onClick={salvaCredenziali}>
              {savingCred ? "Salvataggio…" : "💾 Imposta credenziali"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AziendaDettaglio;
